import join from 'url-join';

import { http } from '../../util/index';
import { asyncAction } from '../../util/actions';
import { handleError, aggregateDataByEntity } from '../util';
import { showToast } from '../../components/toast/actions';
import TranslatedCopy from '../../intl/TranslatedCopy';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

export const fetchAllProductContractGroups = () =>
  aggregateDataByEntity({
    baseName: 'FETCH_ALL_PRODUCT_CONTRACT_GROUPS',
    entity: 'contractGroups',
    constant: 30
  });

/**
 * @name requestProductContractGroupById
 * @async
 * @desc fetch product contract group that match given id
 * @param {string} groupId the product contract group id to request
 */
export const requestProductContractGroupById = asyncAction({
  baseName: 'REQUEST_PRODUCT_CONTRACT_GROUP_BY_ID',
  action: action => groupId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/contractGroups?filter=id(${groupId})`))
      .then(({ data: { objects } }) => {
        dispatch(action.success(objects));

        return objects;
      })
      .catch(error => {
        handleError(dispatch, action, error);

        return [];
      });
  }
});

/**
 * @name createProductContractGroups
 * @async
 * @desc create a new productContractGroup
 * @param {object} productContractGroupsData the new productContractGroup's properties
 */
export const createProductContractGroups = asyncAction({
  baseName: 'CREATE_PRODUCT_CONTRACT_GROUP',
  action: action => productContractGroupsData => dispatch => {
    dispatch(action.start());

    return http
      .post(join(api2, '/contractGroups'), productContractGroupsData)
      .then(({ data }) => {
        dispatch(action.success(data));
        dispatch(requestProductContractGroupById(data.id));
      })
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name requestContractsById
 * @async
 * @desc fetch all contracts that match given ids and setting default response count to 250
 * @param {string} contractIds the contract ids to request
 */
export const requestContractsById = asyncAction({
  baseName: 'REQUEST_CONTRACTS_BY_ID',
  action: action => contractIds => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/contracts?filter=sourceContractId(${encodeURI(contractIds)})&count=250`))
      .then(({ data: { objects } }) => {
        dispatch(action.success(objects));
        return objects;
      })
      .catch(error => {
        handleError(dispatch, action, error);

        return [];
      });
  }
});

/**
 * @name updateProductContractGroup
 * @async
 * @desc update an existing productContractGroup
 * @param {object} params
 * @param {string} productContractGroupId the productContractGroup's id
 * @param {object} data the productContractGroup's new properties
 */
export const updateProductContractGroup = asyncAction({
  baseName: 'UPDATE_PRODUCT_CONTRACT_GROUP',
  action:
    action =>
    ({ productContractGroupId, data }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .patch(join(api2, `/contractGroups/${productContractGroupId}`), data)
        .then(() => {
          dispatch(action.success());
          dispatch(
            showToast({
              message: new TranslatedCopy({ id: 'global.updated' }).toString(),
              type: 'success'
            })
          );
          dispatch(requestProductContractGroupById(productContractGroupId));
        })
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name deleteProductContractGroup
 * @async
 * @desc delete an existing productContractGroup
 * @param {string} productContractGroupId the productContractGroup's id
 */
export const deleteProductContractGroup = asyncAction({
  baseName: 'DELETE_PRODUCT_CONTRACT_GROUP',
  action:
    action =>
    ({ productContractGroupId }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .delete(join(api2, `/contractGroups/${productContractGroupId}`))
        .then(() => {
          dispatch(action.success());
          dispatch(
            showToast({
              message: 'Product contract group deleted',
              type: 'success'
            })
          );

          return true;
        })
        .catch(error => {
          const { errorMessage, status } = error;

          if (status === 409) {
            dispatch(
              showToast({
                message: errorMessage,
                type: 'success'
              })
            );
          } else {
            handleError(dispatch, action, error);
          }

          return false;
        });
    }
});
